import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  InputBase,
  Paper,
  IconButton,
  useMediaQuery,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import Tab from '@mui/material/Tab';
import { Plus, Search, X, Edit, Trash } from 'lucide-mui';
import type { AppDispatch } from '../../../store';
import templates from './templates/templates';
import {
  fetchTemplates,
  editTemplate as updateTemplate,
  removeTemplate,
  selectAllTemplates,
  selectTemplatesStatus,
  Template,
} from '../templateSlice';

interface TemplateMenuProps {
  onTemplateSelect: (templateId: string) => void;
  onCustomPrompt: () => void;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const TemplateMenu: React.FC<TemplateMenuProps> = ({
  onTemplateSelect,
  onCustomPrompt,
  anchorEl,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [templateTabValue, setTemplateTabValue] = useState('popular');
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null
  );
  const [editedPrompt, setEditedPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');

  const dispatch = useDispatch<AppDispatch>();
  const savedTemplates = useSelector(selectAllTemplates);
  const templatesStatus = useSelector(selectTemplatesStatus);
  const isLoadingSavedTemplates = templatesStatus === 'loading';

  useEffect(() => {
    if (templatesStatus === 'idle') {
      dispatch(fetchTemplates());
    }
  }, [dispatch, templatesStatus]);

  const handleTemplateTabChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => {
    setTemplateTabValue(newValue);
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (searchTerm) {
      setTemplateTabValue('all');
    }
  }, [searchTerm]);

  const handleEditClick = (e: React.MouseEvent, template: Template) => {
    e.stopPropagation();
    setEditTemplate(template);
    setEditedPrompt(template.prompt);
    setEditedDescription(template.description || '');
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, template: Template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editTemplate?.id || !editedPrompt.trim()) return;

    setIsSubmitting(true);
    try {
      const resultAction = await dispatch(
        updateTemplate({
          id: editTemplate.id,
          template: {
            prompt: editedPrompt.trim(),
            description: editedDescription.trim(),
          },
        })
      );
      if (updateTemplate.fulfilled.match(resultAction as any)) {
        setIsEditDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to update template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete?.id) return;

    setIsSubmitting(true);
    try {
      const resultAction = await dispatch(removeTemplate(templateToDelete.id));
      if (removeTemplate.fulfilled.match(resultAction as any)) {
        setIsDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTemplates = templates
    .filter(
      (template) =>
        template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.description
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ??
          false)
    )
    .sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return a.title.localeCompare(b.title);
    });

  const featuredTemplates = filteredTemplates.filter(
    (template) => template.featured
  );

  const filteredSavedTemplates = savedTemplates.filter(
    (template) =>
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (template.prompt?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false)
  );

  const allTemplates = [
    ...filteredTemplates,
    ...filteredSavedTemplates,
  ].sort((a, b) => a.title.localeCompare(b.title));

  React.useEffect(() => {
    if (anchorEl && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [anchorEl]);

  const renderSavedTemplates = () => {
    if (isLoadingSavedTemplates) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (savedTemplates.length === 0) {
      return (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            py: 4,
          }}
        >
          No saved templates yet
        </Typography>
      );
    }

    return savedTemplates.map((template) => (
      <MenuItem
        key={template.id}
        onClick={() => onTemplateSelect(template.id!)}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': {
            borderBottom: 'none',
          },
          '&:hover .action-buttons': {
            opacity: 1,
            width: 'auto',
            marginLeft: 1,
          },
          '&:hover .template-text': {
            width: 'calc(100% - 90px)',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            className="template-text"
            sx={{
              flexGrow: 1,
              width: '100%',
              transition: 'width 0.2s',
            }}
          >
            <Typography variant="body1" noWrap>
              {template.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.8,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              noWrap
            >
              {template.description || template.prompt}
            </Typography>
          </Box>
          <Box
            className="action-buttons"
            sx={{
              display: 'flex',
              gap: 1,
              opacity: 0,
              width: 0,
              overflow: 'hidden',
              transition: 'all 0.2s',
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => handleEditClick(e, template)}
              sx={{ color: 'primary.main' }}
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => handleDeleteClick(e, template)}
              sx={{ color: 'error.main' }}
            >
              <Trash />
            </IconButton>
          </Box>
        </Box>
      </MenuItem>
    ));
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={
          isMobile
            ? {
                vertical: 'bottom',
                horizontal: 'center',
              }
            : undefined
        }
        transformOrigin={
          isMobile
            ? {
                vertical: 'top',
                horizontal: 'center',
              }
            : undefined
        }
        slotProps={{
          paper: {
            style: {
              width: isMobile ? `calc(100vw - ${theme.spacing(4)})` : '300px',
              maxWidth: '100%',
              height: '520px',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              pt: 0.5,
              width: '100%',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Paper
              component="form"
              onSubmit={handleSearch}
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.secondary.main}`,
                borderRadius: '8px',
                backgroundColor: theme.palette.background.default,
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1, height: '2.5rem' }}
                placeholder="Search templates..."
                inputProps={{ 'aria-label': 'search templates' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                inputRef={searchInputRef}
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (searchTerm) {
                    setSearchTerm('');
                  }
                }}
                sx={{ p: '10px' }}
                aria-label={searchTerm ? 'clear search' : 'search'}
              >
                {searchTerm ? <X /> : <Search />}
              </IconButton>
            </Paper>
            <MenuItem
              onClick={onCustomPrompt}
              sx={{
                mt: 1.5,
                mb: 0.5,
                border: '1px solid',
                borderColor: 'secondary.light',
                borderRadius: '8px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: 'secondary.main',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                  transform: 'scale(1.01)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                },
                pl: 1.5,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1.5 }}>
                  <Plus sx={{ fontSize: 32 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" noWrap>
                    Custom Template
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.8, fontSize: '0.75rem' }}
                    noWrap
                  >
                    Create your own custom template
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          </Box>
          <TabContext value={templateTabValue}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <TabList
                onChange={handleTemplateTabChange}
                textColor="primary"
                indicatorColor="primary"
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 'bold',
                    },
                    justifyContent: 'center',
                  },
                }}
              >
                <Tab
                  label="Popular"
                  value="popular"
                  sx={{ textTransform: 'none' }}
                />
                <Tab
                  label="Saved"
                  value="saved"
                  sx={{ textTransform: 'none' }}
                />
                <Tab label="All" value="all" sx={{ textTransform: 'none' }} />
              </TabList>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
              }}
            >
              <TabPanel
                value="popular"
                sx={{
                  p: 0,
                  height: '300px',
                  overflow: 'auto',
                }}
              >
                {featuredTemplates.map((template) => (
                  <MenuItem
                    key={template.id}
                    onClick={() => onTemplateSelect(template.id)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Box sx={{ width: '100%', pr: 1 }}>
                      <Typography variant="body1" noWrap>
                        {template.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        noWrap
                      >
                        {template.description || template.prompt}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TabPanel>
              <TabPanel
                value="all"
                sx={{
                  p: 0,
                  height: '300px',
                  overflow: 'auto',
                }}
              >
                {allTemplates.map((template) => (
                  <MenuItem
                    key={template.id}
                    onClick={() => onTemplateSelect(template.id)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Box sx={{ width: '100%', pr: 1 }}>
                      <Typography variant="body1" noWrap>
                        {template.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.8,
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        noWrap
                      >
                        {template.description || template.prompt}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TabPanel>
              <TabPanel
                value="saved"
                sx={{
                  p: 0,
                  height: '300px',
                  overflow: 'auto',
                }}
              >
                {renderSavedTemplates()}
              </TabPanel>
            </Box>
          </TabContext>
        </Box>
      </Menu>

      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            m: isMobile ? 2 : 3,
            width: isMobile ? 'calc(100% - 32px)' : undefined,
            maxHeight: isMobile ? 'calc(100% - 32px)' : undefined,
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1">Edit Template</Typography>
        </Box>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {editTemplate?.title}
            </Typography>
            <TextField
              label="Template Description"
              type="text"
              fullWidth
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="Enter your template description"
            />
            <TextField
              label="Template Content"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={editedPrompt}
              onChange={(e) => setEditedPrompt(e.target.value)}
              placeholder="Enter your template content"
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            color="primary"
            disabled={!editedPrompt.trim() || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template &quot;
            {templateToDelete?.title}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateMenu;
